import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ChangeAgentRequest, ChatConfigRequest, ChatOption, ChatResponse, InitResponse, UserChatPreferences } from '../models/chat.models';
import { SpeechConfig } from "microsoft-cognitiveservices-speech-sdk";
import { ConfigurationService } from './configuration.service';
import { ReservedTags, SkillType, UserSentiment } from '../enums/chat.enum';
import { AuthenticationService } from './authentication.service';
import { AgentType } from '../enums/event.enum';
import { FormDTO, FormRequest } from '../models/form.model';

const CHAT_SESSION_LAST_INTERACTED_DATETIME = "chat_session_last_interacted_datetime";
    
const SESSION_ID_HEADER = "Session-ID";
const REGION_CODE_HEADER = "75F-Region-Code";
const AUTHORIZATION_HEADER = "Authorization";
const CONTENT_TYPE_HEADER = "Content-Type";
const MIME_TYPE_HEADER = "MimeType";

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    private readonly errorSource: Subject<any> = new Subject<any>();
    error$ = this.errorSource.asObservable();
    private sessionId: string;
    private regionCode: string;
    private chatUrl: string;

    constructor(
        private readonly http: HttpClient,
        private readonly configurationService: ConfigurationService,
        private readonly authenticationService: AuthenticationService
    ) {
        const defaultRegion = this.configurationService.getConfig('defaultRegion');
        this.chatUrl = this.configurationService.getConfig(defaultRegion)?.chatServiceUrl ?? '';
        this.regionCode = this.configurationService.getConfig(defaultRegion)?.regionCode ?? '';
    }

    get jsonTypeBaseHeaders(): HttpHeaders {
        return new HttpHeaders({
            [CONTENT_TYPE_HEADER]: "application/json",
            [AUTHORIZATION_HEADER]: `Bearer ${this.bearerToken}`,
            [REGION_CODE_HEADER]: this.regionCode
        });
    }

    get jsonTypeHeaders(): HttpHeaders {
        return new HttpHeaders({
            [CONTENT_TYPE_HEADER]: "application/json",
            [AUTHORIZATION_HEADER]: `Bearer ${this.bearerToken}`,
            [SESSION_ID_HEADER]: this.sessionId,
            [REGION_CODE_HEADER]: this.regionCode
        });
    }

    get formTypeHeaders(): HttpHeaders {
        return new HttpHeaders({
            [MIME_TYPE_HEADER]: "multipart/form-data",
            [AUTHORIZATION_HEADER]: `Bearer ${this.bearerToken}`,
            [SESSION_ID_HEADER]: this.sessionId,
            [REGION_CODE_HEADER]: this.regionCode
        });
    }

    get bearerToken() {
        return this.authenticationService.bearerToken;
    }

    get chatSupportUrl() {
        return this.configurationService.getConfig('chatSupportUrl');
    }
    
    get speechConfig(): Observable<SpeechConfig> {
        return this.fetchSpeechAPIToken().pipe(
            map((response) => SpeechConfig.fromAuthorizationToken(response.token, response.region))
        );
    }

    setSessionId(sessionId: string) {
        this.sessionId = sessionId;
    }

    // SPEECH RECOGNITION & SYNTHESIS

    fetchSpeechAPIToken(): Observable<any> {
        return this.get(`${this.chatUrl}/speech/token`, { headers: this.jsonTypeBaseHeaders });
    }

    // INITIALIZATION

    initializeSession(config: ChatConfigRequest): Observable<InitResponse> {
        const { chatUrl, regionCode, ...requestParams } = config;
        this.chatUrl = chatUrl;
        this.regionCode = regionCode;
        const request = {
            lastInteractedDateTime: this.lastInteractedDatetime,
            localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...requestParams
        }
        const url = `${this.chatUrl}/chat/init`;
        return this.post(url, request, { headers: this.jsonTypeBaseHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }
    
    // PREFERENCES

    getUserChatPreferences(): Observable<UserChatPreferences> {
        return this.get(`${this.chatUrl}/user-preferences`, { headers: this.jsonTypeBaseHeaders })
    }

    // CHAT

    transferAgent(fromAgent: AgentType, request: ChangeAgentRequest): Observable<ChatResponse> {
        const url = `${this.chatUrl}/chat/agents/${fromAgent.toLowerCase()}/transfer`;
        return this.post(url, request, { headers: this.jsonTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }

    submitMessageToAI(formData: any): Observable<ChatResponse> {
        const url = `${this.chatUrl}/chat/agents/ai`;
        return this.post(url, formData, { headers: this.formTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }

    submitMessageToHuman(formData: any, conversationId: string): Observable<ChatResponse> {
        const url = `${this.chatUrl}/chat/agents/human/${conversationId}`;
        return this.post(url, formData, { headers: this.formTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }

    endAIChatSession(): Observable<ChatResponse> {
        const url = `${this.chatUrl}/chat/agents/ai`;
        return this.delete(url, { headers: this.jsonTypeHeaders });
    }

    // USER-FORM REQUESTS

    submitUserFormIntake(taskType: string, taskId: string, form: FormDTO, submit: boolean = false): Observable<FormRequest> {
        const url = `${this.chatUrl}/chat/tasks/${taskType}/${taskId}?submit=${submit}`;
        return this.post(url, form, { headers: this.jsonTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }

    cancelUserFormIntake(taskType: string, taskId: string): Observable<ChatResponse> {
        const url = `${this.chatUrl}/chat/tasks/${taskType}/${taskId}`;
        return this.delete(url, { headers: this.jsonTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        );
    }
    
    // SKILLS

    getSkills(profileId: string, skillType: SkillType | null = null): Observable<ChatOption[]> {
        let url = `${this.chatUrl}/chat/agents/ai/skills?profile-id=${profileId}`;
        if (skillType) {
            url += `&skill-type=${skillType}`;
        }
        return this.get(url, { headers: this.jsonTypeHeaders }).pipe(
            tap(() => this.saveLastInteractedDatetime())
        )
    }

    /**
     * Utility method to get the display content for a chat option.
     * @param option Chat option
     * @returns Sanitized HTML content
     */
    getDisplayContent(option: ChatOption, fromInitQuickOptions: boolean = false): string {
        let content = option.name;
        if (option.presentationData?.icon && fromInitQuickOptions) {
            content += ` ${option.presentationData.icon}`;
        }
        if (content.includes(ReservedTags.ENTITY_REF)) {
            content = content.replace(ReservedTags.ENTITY_REF, '<span class="mention-item">@EQUIPMENT</span>');
        }
        return content;
    }

    // FEEDBACK

    submitMessageFeedback(messageId: string, userSentiment: UserSentiment, userFeedback: string | null = null): Observable<any> {
        const url = `${this.chatUrl}/chat/feedback/${this.sessionId}/${messageId}`;
        const request = {
            userSentiment: userSentiment,
            userFeedback: userFeedback
        }
        return this.post(url, request, { headers: this.jsonTypeBaseHeaders })
    }

    resetMessageFeedback(messageId: string): Observable<any> {
        const url = `${this.chatUrl}/chat/feedback/${this.sessionId}/${messageId}`;
        return this.delete(url, { headers: this.jsonTypeBaseHeaders })
    }
    
    private saveLastInteractedDatetime() {
        localStorage.setItem(CHAT_SESSION_LAST_INTERACTED_DATETIME, new Date().toISOString());
    }

    get lastInteractedDatetime() {
        return localStorage.getItem(CHAT_SESSION_LAST_INTERACTED_DATETIME);
    }

    private get(url: string, options?: any): Observable<any> {
        return this.http.get(url, options).pipe(
            catchError((err) => this.handleError(err))
        );
    }

    private post(url: string, payload: any, options?: any): Observable<any> {
        return this.http.post(url, payload, options).pipe(
            catchError((err) => this.handleError(err))
        );
    }

    private delete(url: string, options?: any): Observable<any> {
        return this.http.delete(url, options).pipe(
            catchError((err) => this.handleError(err))
        );
    }

    private handleError(error: Error): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error);
    }
}
